/**
 * TouchControls — on-screen action buttons for phones and tablets.
 *
 * Stages keep steering on the canvas (drag a finger and the creature
 * follows it) and put their discrete actions — bite, dodge, dash, brake —
 * on these buttons, so nothing needs a right-click or a keyboard.
 *
 * Built only on touch devices; `createTouchPad` returns null elsewhere.
 */

export const IS_TOUCH = typeof window !== 'undefined'
  && (window.matchMedia?.('(hover: none) and (pointer: coarse)').matches
      || 'ontouchstart' in window);

export interface TouchButtonSpec {
  id: string;
  /** Big glyph on the button. */
  icon: string;
  /** Small caption under the glyph. */
  label: string;
  /** Which side of the screen it sits on. */
  side: 'left' | 'right';
  /** Bigger button for the primary action. */
  primary?: boolean;
  onPress: () => void;
  /** For hold-style actions (e.g. brake). */
  onRelease?: () => void;
}

export interface TouchPad {
  /** Grey a button out (e.g. not enough ATP). */
  setReady: (id: string, ready: boolean) => void;
  destroy: () => void;
}

export function createTouchPad(buttons: TouchButtonSpec[]): TouchPad | null {
  if (!IS_TOUCH) return null;
  injectStyle();
  const root = document.createElement('div');
  root.id = 'touch-pad';
  const sides: Record<'left' | 'right', HTMLElement> = {
    left: document.createElement('div'),
    right: document.createElement('div'),
  };
  sides.left.className = 'tp-side tp-left';
  sides.right.className = 'tp-side tp-right';
  root.append(sides.left, sides.right);

  const els = new Map<string, HTMLElement>();
  for (const b of buttons) {
    const btn = document.createElement('button');
    btn.className = 'tp-btn' + (b.primary ? ' tp-primary' : '');
    btn.setAttribute('aria-label', b.label);
    btn.innerHTML = `<span class="tp-icon">${b.icon}</span><span class="tp-label">${b.label}</span>`;
    // pointerdown, not click: no 300ms tap delay, and the canvas never
    // sees the touch (so tapping a button doesn't also steer).
    btn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      btn.classList.add('down');
      b.onPress();
    });
    const up = (e: Event) => {
      e.stopPropagation();
      btn.classList.remove('down');
      b.onRelease?.();
    };
    btn.addEventListener('pointerup', up);
    btn.addEventListener('pointercancel', up);
    btn.addEventListener('pointerleave', up);
    btn.addEventListener('click', (e) => e.stopPropagation());
    els.set(b.id, btn);
    sides[b.side].appendChild(btn);
  }
  document.body.appendChild(root);
  // Lets stage stylesheets make room for the pad (distinct from the
  // permanent `is-touch` body class the mobile viewport sets).
  document.body.classList.add('has-pad');

  return {
    setReady: (id, ready) => els.get(id)?.classList.toggle('tp-dim', !ready),
    destroy: () => { root.remove(); document.body.classList.remove('has-pad'); },
  };
}

function injectStyle(): void {
  if (document.getElementById('touch-pad-styles')) return;
  const style = document.createElement('style');
  style.id = 'touch-pad-styles';
  style.textContent = `
#touch-pad { position: fixed; inset: auto 0 0 0; z-index: 70; display: flex; justify-content: space-between;
  padding: 0 14px calc(14px + env(safe-area-inset-bottom)); pointer-events: none; font-family: 'Inter', system-ui, sans-serif; }
#touch-pad .tp-side { display: flex; gap: 12px; align-items: flex-end; pointer-events: none; }
#touch-pad .tp-btn {
  pointer-events: auto; -webkit-tap-highlight-color: transparent; touch-action: none;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
  width: 74px; height: 74px; border-radius: 50%; font: inherit;
  background: rgba(14, 8, 4, 0.55); border: 2px solid rgba(255, 208, 112, 0.5); color: #fff5dc;
  backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); transition: transform 0.08s, background 0.08s, opacity 0.15s;
}
#touch-pad .tp-primary { width: 96px; height: 96px; border-color: rgba(255, 160, 80, 0.8); background: rgba(60, 20, 8, 0.6); }
#touch-pad .tp-btn.down { transform: scale(0.92); background: rgba(255, 208, 112, 0.28); }
#touch-pad .tp-btn.tp-dim { opacity: 0.35; }
#touch-pad .tp-icon { font-size: 26px; line-height: 1; }
#touch-pad .tp-primary .tp-icon { font-size: 34px; }
#touch-pad .tp-label { font-size: 9px; font-weight: 700; letter-spacing: 0.14em; opacity: 0.85; }
`;
  document.head.appendChild(style);
}
